import { Component } from '@angular/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';

import { SnackBarConfig, EExcelFileExtension } from '../../../shared';
import { ExportApiService } from '../../../shared/services/api/export-api.service';
import { ApiService } from '../../../shared/services/api/api.service';
import { EnvironmentUtilService } from '../../../shared/services/util';

@Component({
  selector: 'lum-consumption-export',
  templateUrl: './consumption-export.component.html',
  styleUrls: ['./consumption-export.component.scss'],
})
export class ConsumptionExportComponent {
  selectedFormat: 'xlsx' | 'csv' = 'xlsx';
  selectedStartDate: Date | null = null;
  selectedEndDate: Date | null = null;
  isExporting = false;
  
  // Transaction type filters (default all checked)
  transactionTypes = {
    sale: true,
    assembly: true,
    disassembly: true,
    adjustment: true
  };
  
  private environment = this.environmentUtilService.getEnvironment();

  constructor(
    private exportApiService: ExportApiService,
    private snackBar: MatSnackBar,
    private apiService: ApiService,
    private environmentUtilService: EnvironmentUtilService,
  ) {}

  get hasSelectedTransactionTypes(): boolean {
    return Object.values(this.transactionTypes).some(selected => selected);
  }

  onStartDateChange(event: MatDatepickerInputEvent<Date>): void {
    if (event.value) {
      this.selectedStartDate = new Date(event.value);
    }
  }

  onEndDateChange(event: MatDatepickerInputEvent<Date>): void {
    if (event.value) {
      this.selectedEndDate = new Date(event.value);
    }
  }

  exportConsumptionData(): void {
    this.isExporting = true;

    const params: { [key: string]: string } = {
      format: this.selectedFormat,
      group: 'day_product', // Group by day then by SKU
    };

    // Add start date filter if selected
    if (this.selectedStartDate) {
      params['date[gte]'] = this.selectedStartDate.toISOString();
    }

    // Add end date filter if selected
    if (this.selectedEndDate) {
      params['date[lte]'] = this.selectedEndDate.toISOString();
    }

    // Add transaction type filters
    const selectedTypes = Object.entries(this.transactionTypes)
      .filter(([type, selected]) => selected)
      .map(([type, selected]) => type);
    
    if (selectedTypes.length > 0 && selectedTypes.length < 4) {
      // Only add type filter if not all types are selected
      params['type'] = selectedTypes.join(',');
    }

    // Show immediate feedback to user
    this.snackBar.open('Preparing export... This may take a moment for large datasets', 'Info', {
      duration: 3000,
    });

    // Use ApiService to handle authentication and streaming properly
    this.apiService.getBlob('/api/admin/inventory/transactions/consumption/export', {
      params: params,
    }).subscribe({
      next: (response) => {
        this.isExporting = false;
        
        if (response && response instanceof Blob) {
          this.snackBar.open('Export ready! Download starting now...', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });

          const timestamp = new Date().toISOString().split('T')[0];
          const fileName = `consumption-transactions-${timestamp}`;
          const extension = this.selectedFormat === 'csv' ? 'csv' : EExcelFileExtension.XLSX;
          
          this.exportApiService.saveBlobAsFile(
            response,
            fileName,
            extension,
          );
        } else {
          this.snackBar.open('No data found for export', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        }
      },
      error: (error) => {
        this.isExporting = false;
        console.error('Export error:', error);
        
        const errorMessage = error?.error?.message || 
                           error?.message || 
                           'Error exporting consumption data';
        
        this.snackBar.open(errorMessage, 'Error', {
          duration: SnackBarConfig.DEFAULT.duration,
        });
      },
    });
  }
} 